<script lang="ts" setup>
import { createSidebar, NavigationSidebar, VIcon, VLoader } from "@app/core";
import { computed } from "vue";
import { RouterView } from "vue-router";

import router from "@/app/router";
import { useMenu } from "@/app/router/composables/useMenu.ts";
import { WorkspaceContextBlock } from "@/features/organization";
import { prefetchRoute } from "@/shared/config/prefetch.ts";
import { useGlobalFiltersStore } from "@/shared/stores/useGlobalFiltersStore";
import { useUserStore } from "@/shared/stores/useUserStore";
import ThemeToggle from "@/shared/ui/ThemeToggle.vue";
import QuickSearch from "@/widget/Search/QuickSearch.vue";

const globalFiltersStore = useGlobalFiltersStore();
const userStore = useUserStore();
const { menuItems } = useMenu();

const sidebar = createSidebar({
  items: menuItems,
  brandName: "Prism",
  storageKey: "pim-sidebar",
  persistCollapse: true,
  persistentQueryParams: ["status", "locale"],
});

// Content area shifts right to accommodate the fixed sidebar
const contentMargin = computed(() => ({
  "lg:ml-64": !sidebar.isCollapsed.value,
  "lg:ml-[4.5rem]": sidebar.isCollapsed.value,
}));
</script>

<template>
  <div class="flex min-h-screen bg-mainBg text-mainText overflow-x-hidden p-4">
    <!-- ── Sidebar ────────────────────────────────────────────────────────── -->
    <NavigationSidebar
      :sidebar="sidebar"
      @prefetch="(to) => prefetchRoute(router, to)"
    >
      <template #header-title>
        <span
          class="sidebar-brand-text overflow-hidden text-ellipsis whitespace-nowrap
                 text-[1.2rem] font-bold tracking-tight"
        >
          Prism
        </span>
      </template>
      <template #subtitle-block>
        <WorkspaceContextBlock :collapsed="sidebar.isCollapsed.value" />
      </template>
      <template #footer-end>
        <div>
          <ThemeToggle :compact="sidebar.isCollapsed.value" />
        </div>
      </template>
    </NavigationSidebar>

    <!-- ── Content Area ───────────────────────────────────────────────────── -->
    <div
      :class="contentMargin"
      class="flex-1 flex flex-col transition-all duration-300 overflow-x-hidden
              min-h-[calc(100vh-2rem)] gap-4 shadow-md rounded-xl"
    >
      <!-- Mobile header — visible only on <lg screens -->
      <header class="bg-secondaryBg border-b border-base-300 flex-shrink-0 lg:hidden rounded-t-xl">
        <div class="flex items-center justify-between h-16 px-4">
          <button
            aria-label="Open menu"
            class="p-2 rounded-lg hover:bg-base-200 text-neutral transition-colors"
            type="button"
            @click="sidebar.toggleMobile()"
          >
            <VIcon
              :size="24"
              icon="mdi:menu"
            />
          </button>
          <span class="text-lg font-semibold text-primary">Prism</span>
          <div class="w-10" />
        </div>
      </header>

      <!-- Page content — rendered only after global filters are ready -->
      <main
        v-if="globalFiltersStore.isInitialized"
        class="flex-1 flex flex-col overflow-x-hidden bg-secondaryBg"
      >
        <div class="h-14 flex items-center pt-4 px-4 sm:px-6 flex-shrink-0">
          <QuickSearch />
        </div>
        <div class="flex-1 py-4 px-4 sm:px-6 flex flex-col min-w-0">
          <RouterView v-slot="{ Component, route }">
            <component
              :is="Component"
              :key="`${route.path}-${userStore.orgId}`"
              class="flex-1 min-w-0"
            />
          </RouterView>
        </div>
      </main>

      <!-- Filters initialising loader -->
      <div
        v-else
        class="flex-1 flex justify-center items-center"
      >
        <VLoader />
      </div>
    </div>
  </div>
</template>

<style scoped>
.sidebar-brand-text {
  background: linear-gradient(135deg, #87CEEB 0%, #4A90E2 50%, #00008B 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

</style>
